/**
 * Doctrine requirements — the machine-readable layer of doctrine that feeds the
 * deterministic rules (the other layer is cited RAG text, Epic 5). Each
 * requirement traces to a source document so insights can cite it.
 *
 * Epic 9: baOperatorRequirement() now accepts an optional resolved ResolvedParam
 * from the requirements engine, giving the rule engine a live, provenance-aware
 * source title instead of the static stand-in fallback.
 *
 * POC sources are clearly-labelled stand-ins to be replaced with authoritative
 * doctrine.
 */

import type { DutyConfig } from './config.js';
import type { ResolvedParam } from './requirements.js';

export interface DoctrineSource {
  docId: string;
  title: string;
  /** Whether this is a fabricated stand-in (always true for the POC). */
  standIn: boolean;
}

export interface CapabilityRequirement {
  capability: 'BAOperator';
  min: number;
  source: DoctrineSource;
}

export const BA_REQUIREMENT_SOURCE: DoctrineSource = {
  docId: 'SOP-SF-STANDIN-001',
  title: 'NSW structure-fire BA guidance (stand-in)',
  standIn: true,
};

/**
 * The minimum-BA-operators requirement for a structure fire.
 *
 * When a resolved ResolvedParam is provided (Epic 9 requirements engine), its
 * value and source title override the config default and static stand-in,
 * giving the insight card a live, provenance-aware citation.
 */
export function baOperatorRequirement(
  config: DutyConfig,
  resolved?: ResolvedParam,
): CapabilityRequirement {
  if (resolved) {
    return {
      capability: 'BAOperator',
      min: resolved.value,
      source: {
        docId: resolved.requirementId,
        title: resolved.sourceTitle,
        standIn: resolved.standIn,
      },
    };
  }
  return {
    capability: 'BAOperator',
    min: config.capability.minBaOperatorsStructureFire,
    source: BA_REQUIREMENT_SOURCE,
  };
}

export interface DistanceRequirement {
  minMetres: number;
  source: DoctrineSource;
}

export const EV_STORAGE_EXCLUSION_SOURCE: DoctrineSource = {
  docId: 'AFAC-3096',
  title: 'AFAC — Incidents Involving Electric Vehicles, Guideline v1.0 (2022) (stand-in)',
  standIn: true,
};

/**
 * The minimum separation distance for a fire-damaged/suspected-damaged EV/HV
 * battery from any exposure (AFAC-3096, stated twice in the same document —
 * see the ev-battery-storage-exclusion DoctrineRequirement). Mirrors
 * baOperatorRequirement()'s resolved-vs-default pattern.
 */
export function evStorageExclusionRequirement(
  config: DutyConfig,
  resolved?: ResolvedParam,
): DistanceRequirement {
  if (resolved) {
    return {
      minMetres: resolved.value,
      source: {
        docId: resolved.requirementId,
        title: resolved.sourceTitle,
        standIn: resolved.standIn,
      },
    };
  }
  return {
    minMetres: config.ev.storageExclusionDistanceM,
    source: EV_STORAGE_EXCLUSION_SOURCE,
  };
}
